import { IGroupListItemDef } from "@components/configurationList";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy, IReportVariantDef } from "@components/smart/smartTable";
import { ElectronicSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setReportDef } from "@pages/reports/Report.test.utils";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { Sort } from "../../../enums";
import { TRecordType } from "../../../global.types";
import {
    vatPeriodParameterOnBeforeLoadCallback,
    vatStatementPeriodParamDef
} from "../customFilterComponents/VatPeriodParameter";
import {
    IReportTableDefinition,
    NumberAggregationFunction,
    ReportConfigGroup,
    TGetReportConfigListItemOverride,
    TimeAggregationFunction,
    TReportColumnOverrides,
    TReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";
import { getVatReportColumnOverrides, getVatStatementStatusFilterDef, VatReportProps } from "../VatReport.utils";

export enum VatVIESStatementReportVariant {
    Evala = "-132"
}

export const VAT_VIES_STATEMENT_REPORT_PATH = "VatVIESStatementReport";

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const tableId = ReportId.VatVIESStatement;
    const title = i18next.t("Reporting:VatVIESStatement.Title");
    const path = VAT_VIES_STATEMENT_REPORT_PATH;
    const initialSortBy = [{ id: "VatClassification_VatViesCode", sort: Sort.Asc }];
    const parameters: string[] = [
        VatReportProps.vatStatementPeriod,
        VatReportProps.vatStatementStatus
    ];

    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                VatReportProps.vatStatementPeriod,
                VatReportProps.vatStatementStatus
            ],
            filterDefinition: {
                [VatReportProps.vatStatementPeriod]: vatStatementPeriodParamDef(),
                [VatReportProps.vatStatementStatus]: getVatStatementStatusFilterDef()
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            defaultFilters: [],
            filterDefinition: {}
        }
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await vatPeriodParameterOnBeforeLoadCallback(storage, ElectronicSubmissionTypeCode.VatVIESStatement);
    };

    const defaultReportVariants: TRecordType<IReportVariantDef> = {
        [VatVIESStatementReportVariant.Evala]: {
            ReportHierarchy: {
                "Aggregate": true,
                "Groups": [
                    {
                        "ColumnAlias": "BusinessPartner_TaxNumber"
                    },
                    {
                        "ColumnAlias": "VatClassification_VatViesCode"
                    }
                ],
                "Columns": [
                    {
                        "ColumnAlias": "Document_NumberOurs"
                    },
                    {
                        "ColumnAlias": "Document_DateTaxableSupply",
                        "AggregationFunction": TimeAggregationFunction.Day
                    },
                    {
                        "ColumnAlias": "VatStatementSection_Name"
                    },
                    {
                        "ColumnAlias": "BusinessPartner_Name"
                    },
                    {
                        "ColumnAlias": "BusinessPartner_CountryCode"
                    }
                ],
                "Aggregations": [
                    {
                        "ColumnAlias": "Document_AmountNet",
                        "AggregationFunction": NumberAggregationFunction.Sum
                    },
                    {
                        "ColumnAlias": "Document_NumberOurs",
                        "AggregationFunction": NumberAggregationFunction.Count
                    }
                ]
            }
        }
    };

    const configListItemsOverride: TGetReportConfigListItemOverride = (item: IGroupListItemDef): TReportConfigListItemOverride => {
        switch (true) {
            case item.id === "BusinessPartner_TaxNumber":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    group: ReportConfigGroup.Groups
                };
            case item.id === "VatClassification_VatViesCode":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    group: ReportConfigGroup.Groups
                };
        }

        return item;
    };

    const columnOverrides: TReportColumnOverrides = {
        ...getVatReportColumnOverrides()
    };

    const defaultReportHierarchy: IReportHierarchy = defaultReportVariants[VatVIESStatementReportVariant.Evala].ReportHierarchy;

    return {
        title, path, id: tableId, initialSortBy, filterBarDef, parameters, columnOverrides,
        onBeforeLoad, defaultReportHierarchy, defaultReportVariants, configListItemsOverride
    };
};

setReportDef(ReportId.VatVIESStatement, getDefinition);